import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { useEditor, EditorContent, Extension } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, useRef } from 'react';
import { BubbleMenu } from '@/Domain/components/Shared/Editor/BubbleMenu/BubbleMenu';

type Props = {
  value: string;
  onChange: (html: string) => void;
  placeholder: string;
  extensions?: Extension[];
  enableBubbleMenu?: boolean;
  editable?: boolean;
};

export const TiptapEditor = ({
  value,
  onChange,
  placeholder,
  extensions = [],
  enableBubbleMenu = true,
  editable = true,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const editor = useEditor({
    editable,
    extensions: [
      StarterKit,
      Underline,
      Link,
      Placeholder.configure({
        placeholder,
        emptyEditorClass:
          'before:content-[attr(data-placeholder)] before:text-foreground-subtle before:absolute before:pointer-events-none',
      }),
      ...extensions,
    ],
    content: value,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML().trim();

      const isEmpty = editor.isEmpty || editor.getText().trim() === '';
      onChange(isEmpty ? '' : html);
    },
  });

  // Sync editor content when the value prop changes externally (e.g. form reset with async data).
  // Skip only when the editor is focused with actual content, to avoid disrupting the user mid-type.
  // Still sync when empty: the user may have clicked before the async form reset completed.
  useEffect(() => {
    if (!editor) return;
    if (editor.isFocused && !editor.isEmpty) return;
    const editorHTML = editor.isEmpty ? '' : editor.getHTML();
    if (value === editorHTML || (!value && editor.isEmpty)) return;
    editor.commands.setContent(value || '', false);
  }, [value, editor]);

  return (
    <div
      ref={containerRef}
      className="w-full px-2.5 py-1.5 text-sm border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent min-h-[32px] align-middle relative mt-2 focus-within:ring-[3px] focus-within:ring-ring/25 focus-within:!border-foreground"
    >
      {editor && enableBubbleMenu && editable && (
        <BubbleMenu editor={editor} containerRef={containerRef} />
      )}
      <EditorContent
        editor={editor}
        className="prose-sm [&_.ProseMirror]:min-h-[21px] [&_.ProseMirror]:overflow-hidden [&_.ProseMirror]:outline-none [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-4 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-4 [&_.ProseMirror_a]:underline [&_.ProseMirror_a]:decoration-primary [&_.ProseMirror_a]:text-primary"
      />
    </div>
  );
};
